/**
 * Creation reason types
 * Surce of truth https://github.com/voiapp/repairs/blob/master/internal/prototype/domain/creation_reasons.go
 * Documentation https://voidev.atlassian.net/wiki/spaces/RND/pages/81269030949/Creation+Reasons+-+Active+List
 * Error codes omitted here as they are not used in manual flow
 */

/**
 * Main task creation reason types
 */
export enum CreationReasonType {
    CUSTOMER_FEEDBACK = 'CUSTOMER_FEEDBACK',
    HARDWARE_SIGNAL = 'HARDWARE_SIGNAL',
    RIDER_BEHAVIOR = 'RIDER_BEHAVIOR',
    PREVENTATIVE_MAINTENANCE = 'PREVENTATIVE_MAINTENANCE',
    OPERATIONAL_CHANGE = 'OPERATIONAL_CHANGE',
    INCIDENT_REPORT = 'INCIDENT_REPORT',
    FIELD_REPORT = 'FIELD_REPORT',
    OTHER = 'OTHER',
}

/**
 * Customer feedback details
 */
export enum CustomerFeedbackDetail {
    BRAKES = 'BRAKES',
    THROTTLE = 'THROTTLE',
    WHEELS = 'WHEELS',
    FENDER = 'FENDER',
    HANDLEBARS = 'HANDLEBARS',
    STEERING = 'STEERING',
    FRAME = 'FRAME',
    BATTERY = 'BATTERY',
    MOTOR = 'MOTOR',
    LIGHTS = 'LIGHTS',
    PHONE_HOLDER = 'PHONE_HOLDER',
    KICKSTAND = 'KICKSTAND',
    SLOW = 'SLOW',
    WONT_START = 'WONT_START',
    STOPPED_DURING_RIDE = 'STOPPED_DURING_RIDE',
    DAMAGED = 'DAMAGED',
    DIRTY = 'DIRTY',
    LOOSE_PARTS = 'LOOSE_PARTS',
    NOISES = 'NOISES',
    WONT_UNLOCK = 'WONT_UNLOCK',
    NOT_STABLE = 'NOT_STABLE',
    PEDALS = 'PEDALS',
    SEAT = 'SEAT',
    WIRES = 'WIRES',
    INCONSISTENT_SPEED = 'INCONSISTENT_SPEED',
}

/**
 * Hardware signal details
 */
export enum HardwareSignalDetail {
    BRAKES = 'BRAKES',
    THROTTLE = 'THROTTLE',
    WHEELS = 'WHEELS',
    MOTOR = 'MOTOR',
    BATTERY = 'BATTERY',
    SLOW = 'SLOW',
    WONT_START = 'WONT_START',
    STOPPED_DURING_RIDE = 'STOPPED_DURING_RIDE',
    ACCELEROMETER = 'ACCELEROMETER',
}

/**
 * Rider behavior details
 */
export enum RiderBehaviorDetail {
    SHORT_RIDES = 'SHORT_RIDES',
    VEHICLE_SWITCHING = 'VEHICLE_SWITCHING',
    TOPPLED_DURING_RIDE = 'TOPPLED_DURING_RIDE',
}

/**
 * Preventative maintenance details
 */
export enum PreventativeMaintenanceDetail {
    STEERING_COLUMN = 'STEERING_COLUMN',
    FRAME_DOWNTUBE = 'FRAME_DOWNTUBE',
    FRONT_FORK = 'FRONT_FORK',
    DISTANCE_THRESHOLD = 'DISTANCE_THRESHOLD',
    TIME_THRESHOLD = 'TIME_THRESHOLD',
}

/**
 * Operational change details
 */
export enum OperationalChangeDetail {
    FLEET_SWAP = 'FLEET_SWAP',
    TRANSPORT = 'TRANSPORT',
    WINTER_STORAGE = 'WINTER_STORAGE',
    MOVE_TO_REPAIR = 'MOVE_TO_REPAIR',
    PROJECT_LEAP = 'PROJECT_LEAP',
    MARKETING = 'MARKETING',
}

/**
 * Incident report details
 */
export enum IncidentReportDetail {
    ACCIDENT = 'ACCIDENT',
    VANDALISM = 'VANDALISM',
}

/**
 * Union type for all detail enums
 */
export type CreationReasonDetail =
    | CustomerFeedbackDetail
    | HardwareSignalDetail
    | RiderBehaviorDetail
    | PreventativeMaintenanceDetail
    | OperationalChangeDetail
    | IncidentReportDetail

/**
 * Mapping of creation reason types to their available details
 */
export const CREATION_REASON_DETAILS_MAP: Record<CreationReasonType, CreationReasonDetail[]> = {
    [CreationReasonType.CUSTOMER_FEEDBACK]: Object.values(CustomerFeedbackDetail),
    [CreationReasonType.HARDWARE_SIGNAL]: Object.values(HardwareSignalDetail),
    [CreationReasonType.RIDER_BEHAVIOR]: Object.values(RiderBehaviorDetail),
    [CreationReasonType.PREVENTATIVE_MAINTENANCE]: Object.values(PreventativeMaintenanceDetail),
    [CreationReasonType.OPERATIONAL_CHANGE]: Object.values(OperationalChangeDetail),
    [CreationReasonType.INCIDENT_REPORT]: Object.values(IncidentReportDetail),
    [CreationReasonType.FIELD_REPORT]: Object.values(CustomerFeedbackDetail), // Mostly same as customer_feedback
    [CreationReasonType.OTHER]: [], // Details omitted for now
}

/**
 * Helper function to get details for a creation reason type
 */
export const getCreationReasonDetails = (reasonType: CreationReasonType): CreationReasonDetail[] => {
    return CREATION_REASON_DETAILS_MAP[reasonType] || []
}

/**
 * Helper function to check if a detail is valid for a reason type
 */
export const isValidDetailForReason = (reasonType: CreationReasonType, detail: CreationReasonDetail): boolean => {
    const validDetails = getCreationReasonDetails(reasonType)
    return validDetails.includes(detail)
}

/**
 * Type for creation reason with optional detail
 */
export interface CreationReason {
    type: CreationReasonType
    detail?: CreationReasonDetail
}

/**
 * Task types that support creation reason selection
 * TT = transport, IFQC = in_field_quality_check
 */
export type TaskTypeWithCreationReason = 'transport' | 'in_field_quality_check'

/**
 * Mapping of task types to their allowed creation reason types
 */
export const TASK_TYPE_CREATION_REASONS_MAP: Record<TaskTypeWithCreationReason, CreationReasonType[]> = {
    transport: [
        CreationReasonType.CUSTOMER_FEEDBACK,
        CreationReasonType.PREVENTATIVE_MAINTENANCE,
        CreationReasonType.OPERATIONAL_CHANGE,
        CreationReasonType.INCIDENT_REPORT,
        CreationReasonType.FIELD_REPORT,
        CreationReasonType.OTHER,
    ],
    in_field_quality_check: [
        CreationReasonType.CUSTOMER_FEEDBACK,
        CreationReasonType.PREVENTATIVE_MAINTENANCE,
        CreationReasonType.INCIDENT_REPORT,
        CreationReasonType.FIELD_REPORT,
        CreationReasonType.OTHER,
    ],
}

/**
 * Helper function to get allowed creation reason types for a task type
 */
export const getAllowedCreationReasonsForTaskType = (taskType: TaskTypeWithCreationReason): CreationReasonType[] => {
    return TASK_TYPE_CREATION_REASONS_MAP[taskType] || []
}

/**
 * Helper function to check if a creation reason type is allowed for a task type
 */
export const isCreationReasonAllowedForTaskType = (
    taskType: TaskTypeWithCreationReason,
    reasonType: CreationReasonType,
): boolean => {
    const allowedReasons = getAllowedCreationReasonsForTaskType(taskType)
    return allowedReasons.includes(reasonType)
}
